import { CheckCircle2, ExternalLink, Sparkles } from 'lucide-react';
import { useDisplayMode } from 'react-openai-apps-sdk';

type PromoVariant = 'default';

interface KontentinoPromoCardProps {
  /**
   * Variant of the promotional card (currently only 'default')
   */
  variant?: PromoVariant;
  /**
   * Custom title for the promo card
   */
  title?: string;
  /**
   * Custom description for the promo card
   */
  description?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const DEFAULT_PROMO_CONTENT = {
  title: 'Need Professional Workflows?',
  description: 'Professional approval workflows, direct publishing, and team collaboration for agencies.',
  features: [
    'Direct publishing to all social platforms',
    'In-app approval workflows & notifications',
    'Team collaboration with real-time updates',
  ],
} as const;

export function KontentinoPromoCard({
  title,
  description,
  className = '',
}: KontentinoPromoCardProps) {
  const displayMode = useDisplayMode();
  const isExpanded = displayMode === 'fullscreen';

  const content = {
    title: title || DEFAULT_PROMO_CONTENT.title,
    description: description || DEFAULT_PROMO_CONTENT.description,
    features: DEFAULT_PROMO_CONTENT.features,
  };

  return (
    <div className={`p-3 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border-2 border-primary/20 rounded-lg ${className}`}>
      <div className="flex items-start gap-2.5">
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-primary-foreground" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="font-bold text-foreground mb-1">{content.title}</h3>
          <p className="text-sm text-muted-foreground mb-2">{content.description}</p>

          {isExpanded && (
            <ul className="text-sm text-muted-foreground space-y-1.5 mb-3">
              {content.features.map((feature, index) => (
                <li key={index} className="flex items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          )}

          <a
            href="https://www.kontentino.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-1.5 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-all font-semibold shadow-sm hover:shadow-md group w-full justify-center"
          >
            <span>More at kontentino.com</span>
            <ExternalLink className="h-4 w-4 opacity-70 group-hover:opacity-100 transition-opacity" />
          </a>
        </div>
      </div>
    </div>
  );
}
